import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useBillingPipelineData, type BillingPipelineView } from '../data/billingApi.ts';
import { formatCurrency, getBillingQueue } from '../data/model';
import { useAppSettingsData } from '../data/settingsApi.ts';
import type { DemoSnapshot } from '../data/types';
import { billingViewLabel, formatDateTimeLabel, getBillingItemClass, matchesBillingSearch, parseCurrencyLabel } from '../utils';

export function BillingPage(props: {
  snapshot: DemoSnapshot;
  apiEnabled: boolean;
  onOpenWorkOrderById: (workOrderId: string) => void;
}) {
  const { settings } = useAppSettingsData(props.apiEnabled);
  const [stageFilter, setStageFilter] = useState<BillingPipelineView>('queue');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const { pipeline: billingItems, isLoading: pipelineLoading, error: pipelineError } = useBillingPipelineData(
    props.apiEnabled,
    'all',
  );

  useEffect(() => {
    if (!settings?.preferences.billingView) return;
    setStageFilter(settings.preferences.billingView as BillingPipelineView);
  }, [settings?.preferences.billingView]);

  const readyToInvoiceOrders = props.snapshot.workOrders.filter((order) =>
    order.status.toLowerCase().includes('prêt') || order.status.toLowerCase().includes('factur'),
  );
  const apiInvoices = billingItems.filter((item) => item.invoiceNumber);
  const billedAmountCents = props.apiEnabled ? apiInvoices.reduce((sum, item) => sum + item.revenueCents, 0) : 0;
  const localPendingAmountCents = readyToInvoiceOrders.reduce((sum, order) => sum + parseCurrencyLabel(order.revenue), 0);
  const effectiveBillingItems = props.apiEnabled ? billingItems : [];
  const queueItems = effectiveBillingItems.filter((item) => item.billingStage !== 'invoiced');
  const readyItems = effectiveBillingItems.filter((item) => item.billingStage === 'ready');
  const blockedItems = effectiveBillingItems.filter((item) => item.billingStage === 'blocked');
  const readyAmountCents = readyItems.reduce((sum, item) => sum + item.revenueCents, 0);
  const invoiceCards = props.apiEnabled
    ? apiInvoices.map((item) => ({
        key: item.workOrderId,
        invoiceNumber: item.invoiceNumber ?? 'N/D',
        status: 'émise',
        workOrderId: item.workOrderId,
        createdAt: item.invoiceCreatedAt ?? item.updatedAt,
        amountCents: item.revenueCents,
      }))
    : [];
  const filteredBillingItems = useMemo(
    () =>
      effectiveBillingItems.filter((item) => {
        if (stageFilter === 'queue' && item.billingStage === 'invoiced') return false;
        if (stageFilter !== 'queue' && stageFilter !== 'all' && item.billingStage !== stageFilter) return false;
        return matchesBillingSearch(item, searchQuery);
      }),
    [effectiveBillingItems, searchQuery, stageFilter],
  );
  const pageSize = 5;
  const pageCount = Math.max(1, Math.ceil(filteredBillingItems.length / pageSize));
  const visibleBillingItems = useMemo(
    () => filteredBillingItems.slice((pageIndex - 1) * pageSize, pageIndex * pageSize),
    [filteredBillingItems, pageIndex],
  );

  useEffect(() => {
    setPageIndex(1);
  }, [props.apiEnabled, searchQuery, stageFilter]);

  useEffect(() => {
    setPageIndex((current) => Math.min(current, pageCount));
  }, [pageCount]);

  const billingPageStart = filteredBillingItems.length ? (pageIndex - 1) * pageSize + 1 : 0;
  const billingPageEnd = Math.min(pageIndex * pageSize, filteredBillingItems.length);
  const billingPageSummary = filteredBillingItems.length
    ? `Affichage ${billingPageStart}-${billingPageEnd} sur ${filteredBillingItems.length} dossiers`
    : 'Aucun dossier pour ce filtre';

  const downloadCsv = () => {
    if (!filteredBillingItems.length) return;

    const escapeCell = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const csv = [
      ['OT', 'Client', 'Site', 'Technicien', 'Étape', 'Action recommandée', 'Montant', 'Facture', 'Actualisé'],
      ...filteredBillingItems.map((item) => [
        item.workOrderId,
        item.clientName,
        item.siteName,
        item.technicianName ?? 'Affectation à confirmer',
        item.billingStageLabel,
        item.recommendedAction,
        formatCurrency(item.revenueCents),
        item.invoiceNumber ?? '',
        formatDateTimeLabel(item.updatedAt),
      ]),
    ]
      .map((row) => row.map((cell) => escapeCell(cell)).join(','))
      .join('\n');

    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `fst-billing-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="page-stack">
      <div className="mini-metric-grid">
        <article className="mini-metric">
          <span className="mini-metric__label">Factures émises</span>
          <strong>{props.apiEnabled ? apiInvoices.length : 0}</strong>
          <span>{props.apiEnabled ? 'Dossiers effectivement facturés dans le pipeline' : 'API facturation inactive'}</span>
        </article>
        <article className="mini-metric">
          <span className="mini-metric__label">Montant facturé</span>
          <strong>{formatCurrency(billedAmountCents)}</strong>
          <span>{props.apiEnabled ? 'Somme des dossiers marqués facturés' : 'Lecture API requise'}</span>
        </article>
        <article className="mini-metric">
          <span className="mini-metric__label">File active</span>
          <strong>{props.apiEnabled ? queueItems.length : readyToInvoiceOrders.length}</strong>
          <span>{props.apiEnabled ? billingViewLabel(stageFilter) : 'Dossiers locaux visibles'}</span>
        </article>
        <article className="mini-metric">
          <span className="mini-metric__label">Prêts à facturer</span>
          <strong>{props.apiEnabled ? readyItems.length : readyToInvoiceOrders.length}</strong>
          <span>{props.apiEnabled ? `${blockedItems.length} bloqués actuellement` : 'Lecture locale uniquement'}</span>
        </article>
        <article className="mini-metric">
          <span className="mini-metric__label">CA à déclencher</span>
          <strong>{formatCurrency(props.apiEnabled ? readyAmountCents : localPendingAmountCents)}</strong>
          <span>{props.apiEnabled ? 'Montant des dossiers réellement prêts' : 'Approximation basée sur la file locale'}</span>
        </article>
      </div>

      <div className="two-column-layout">
        <Card title="Dossiers facturés" subtitle="Historique restitué depuis le pipeline consolidé.">
          {props.apiEnabled && pipelineLoading ? (
            <div className="app-empty">
              <strong>Chargement</strong>
              <span>Lecture des dossiers facturés en cours.</span>
            </div>
          ) : null}

          {props.apiEnabled && pipelineError ? (
            <div className="alert-card alert-card--danger">
              <strong>Lecture facturation impossible</strong>
              <span>{pipelineError instanceof Error ? pipelineError.message : 'Erreur inconnue.'}</span>
            </div>
          ) : null}

          {!pipelineLoading && !pipelineError && props.apiEnabled && !apiInvoices.length ? (
            <div className="alert-card alert-card--warning">
              <strong>Aucune facture consolidée</strong>
              <span>Le pipeline ne remonte encore aucune intervention réellement facturée.</span>
            </div>
          ) : null}

          {!props.apiEnabled ? (
            <div className="alert-card alert-card--warning">
              <strong>API facturation inactive</strong>
              <span>Passer en mode HTTP pour charger la vue multi-dossiers consolidée.</span>
            </div>
          ) : null}

          <div className="invoice-list">
            {invoiceCards.map((invoice) => (
              <button
                key={invoice.key}
                type="button"
                className="invoice-item invoice-item--interactive"
                onClick={() => props.onOpenWorkOrderById(invoice.workOrderId)}
              >
                <div className="invoice-item__head">
                  <strong>{invoice.invoiceNumber}</strong>
                  <span className="status-pill status-pill--ready">{invoice.status}</span>
                </div>
                <span>OT {invoice.workOrderId}</span>
                <div className="invoice-item__meta">
                  <span>{formatDateTimeLabel(invoice.createdAt)}</span>
                  <span>{formatCurrency(invoice.amountCents)}</span>
                </div>
              </button>
            ))}
          </div>
        </Card>

        <Card
          title="Pipeline dossiers"
          subtitle={
            props.apiEnabled
              ? "File consolidée depuis `/api/app/billing/pipeline`, enrichie avec l'état MVP lié."
              : 'Repli local limité au scénario démonstrateur.'
          }
        >
          {props.apiEnabled ? (
            <>
              <div className="billing-toolbar">
                <label className="field-stack">
                  <span>Filtre principal</span>
                  <select value={stageFilter} onChange={(event) => setStageFilter(event.target.value as BillingPipelineView)}>
                    <option value="queue">File active</option>
                    <option value="ready">Prêts à facturer</option>
                    <option value="blocked">Bloqués</option>
                    <option value="invoiced">Facturés</option>
                    <option value="all">Tout voir</option>
                  </select>
                </label>
                <label className="field-stack">
                  <span>Recherche</span>
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="OT, client, site, technicien..."
                  />
                </label>
              </div>

              {pipelineLoading ? (
                <div className="app-empty">
                  <strong>Chargement</strong>
                  <span>Construction du pipeline de facturation en cours.</span>
                </div>
              ) : null}

              {pipelineError ? (
                <div className="alert-card alert-card--danger">
                  <strong>Pipeline indisponible</strong>
                  <span>{pipelineError instanceof Error ? pipelineError.message : 'Erreur inconnue.'}</span>
                </div>
              ) : null}

              {!pipelineLoading && !pipelineError ? (
                <>
                  <div className="billing-list">
                    {visibleBillingItems.length ? (
                      visibleBillingItems.map((item) => (
                        <button
                          key={item.workOrderId}
                          type="button"
                          className={`billing-item billing-item--interactive${getBillingItemClass(item.billingStage)}`}
                          onClick={() => props.onOpenWorkOrderById(item.workOrderId)}
                        >
                          <strong>{item.workOrderId} · {item.clientName}</strong>
                          <span>{item.siteName} · {item.technicianName ?? 'Affectation à confirmer'}</span>
                          <span>{item.blocker ?? item.recommendedAction}</span>
                          <div className="billing-item__meta">
                            <span>{formatCurrency(item.revenueCents)}</span>
                            <span>{item.billingStageLabel}</span>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="app-empty">
                        <strong>Aucun dossier pour ce filtre</strong>
                        <span>Changer le filtre ou la recherche pour voir une autre tranche du pipeline.</span>
                      </div>
                    )}
                  </div>

                  <div className="billing-pagination">
                    <div className="billing-pagination__meta">
                      <strong>{billingPageSummary}</strong>
                      <span>Navigation par tranche de cinq dossiers et export CSV du filtrage courant.</span>
                    </div>
                    <div className="billing-pagination__actions">
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => setPageIndex((current) => Math.max(1, current - 1))}
                        disabled={pageIndex <= 1}
                      >
                        Précédent
                      </button>
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => setPageIndex((current) => Math.min(pageCount, current + 1))}
                        disabled={pageIndex >= pageCount}
                      >
                        Suivant
                      </button>
                      <button
                        type="button"
                        className="primary-button"
                        onClick={downloadCsv}
                        disabled={!filteredBillingItems.length}
                      >
                        Exporter CSV
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <div className="billing-list">
              {readyToInvoiceOrders.length ? (
                readyToInvoiceOrders.map((order) =>
                  getBillingQueue(order).map((item) => (
                    <button
                      key={`${order.id}-${item.title}`}
                      type="button"
                      className={`billing-item billing-item--interactive${item.tone ? ` billing-item--${item.tone}` : ''}`}
                      onClick={() => props.onOpenWorkOrderById(order.id)}
                    >
                      <strong>{order.id} · {item.title}</strong>
                      <span>{item.detail}</span>
                      <div className="billing-item__meta">
                        <span>{item.amount}</span>
                        <span>{item.status}</span>
                      </div>
                    </button>
                  )),
                )
              ) : (
                <div className="app-empty">
                  <strong>Rien en attente immédiate</strong>
                  <span>Valider un rapport terrain pour remplir la file de facturation.</span>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
